/**
 * Pype Loader — host-side orchestrator for Pype chat applications.
 *
 * @module
 */

import mitt from 'mitt';

import { buildAppConfig, validateAppConfig } from './config';
import {
  clearStoredSession,
  getStoredSession,
  setStoredSession,
  type StoredSessionData,
} from './session';
import {
  PypeLoaderError,
  LoaderErrorCode,
  LoaderStatus,
  type AppConfig,
  type BootOptions,
  type CreatePypeLoaderOptions,
  type EmbedErrorCode,
  type MetadataPayload,
  type PypeError,
  type LoaderCommand,
  type LoaderEvents,
  type LoaderState,
  type RuntimeUpdatableConfig,
  type SessionInfo,
  type StyleOverrides,
  type BrandingOverrides,
  type WidgetErrorCode,
} from './types';
// Type-only, so nothing here reaches the bundle: the loader still boots the
// widget with a dynamic `import('@pypestream/pype')`. Importing the CONTRACT is
// what makes a widget-side rename fail the loader's own typecheck.
import type { PypeHostElement, PypeViewState } from '@pypestream/pype';
import { validateMetadata } from './metadata-validation';
import { EventListenerBridge } from './register-for-events';
import { openPypeWindow, type OpenWindowResult } from './window-target';

export type { BootOptions, CreatePypeLoaderOptions } from './types';

async function registerPypeWC(): Promise<void> {
  if (typeof customElements === 'undefined') return;
  if (customElements.get('ps-pype')) return;
  // Loads the precompiled `<ps-pype>` bundle (Tailwind + DaisyUI styles
  // already inlined as constructable stylesheet strings). Keeps host
  // applications free of any Tailwind/DaisyUI tooling — the responsibility
  // lives entirely in the @pypestream/pype build.
  //
  // CDN deploy override: the CDN loader bundle assigns
  // `globalThis.__PYPE_RUNTIME_URL__` before this code runs, with a URL
  // resolved from `import.meta.url` so chunks load from the same origin
  // as the loader (per-PR previews self-isolate, prod stays clean). When
  // the global is unset (npm/workspace consumers), we fall back to the
  // bare specifier which the consumer's bundler resolves.
  const runtimeUrl = (globalThis as { __PYPE_RUNTIME_URL__?: string })
    .__PYPE_RUNTIME_URL__;
  if (runtimeUrl) {
    await import(/* @vite-ignore */ runtimeUrl);
  } else {
    await import('@pypestream/pype');
  }
}

/**
 * Resolve the host's container. A string is a CSS selector — the embed snippet
 * Pro Studio generates emits one, so this is the common shape in the wild.
 *
 * An unmatched selector throws instead of falling back to `document.body`.
 * Falling back would mount a fullpage widget over the customer's entire page,
 * which presents as a styling bug and sends the reader looking in the wrong
 * place; `MOUNT_FAILED` names the selector that missed.
 */
function resolveContainer(
  container: HTMLElement | string | undefined,
  loaderStatus: LoaderStatus
): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  if (container === undefined) return document.body;
  if (typeof container !== 'string') return container;

  const found = document.querySelector<HTMLElement>(container);
  if (!found) {
    throw new PypeLoaderError(
      LoaderErrorCode.MOUNT_FAILED,
      `container selector "${container}" matched no element. Check that the ` +
        `element exists before boot() runs — a script in <head>, or one above ` +
        `the markup it targets, runs too early.`,
      { loaderStatus }
    );
  }
  return found;
}

// ---------------------------------------------------------------------------
// State maps
// ---------------------------------------------------------------------------

const ALLOWED_TRANSITIONS: Record<LoaderStatus, LoaderStatus[]> = {
  [LoaderStatus.Idle]: [LoaderStatus.Initializing, LoaderStatus.Error],
  [LoaderStatus.Initializing]: [LoaderStatus.ChatActive, LoaderStatus.Error],
  [LoaderStatus.ChatActive]: [LoaderStatus.ChatEnded, LoaderStatus.Error],
  // `ChatEnded → ChatActive` covers `restart()` from the chat-ended screen:
  // the widget is still mounted, the conversation ended, and a fresh session
  // re-activates the Loader when `<ps-pype>` reports `pype-session-created`.
  [LoaderStatus.ChatEnded]: [
    LoaderStatus.Idle,
    LoaderStatus.Initializing,
    LoaderStatus.ChatActive,
    LoaderStatus.Error,
  ],
  [LoaderStatus.Error]: [LoaderStatus.Idle, LoaderStatus.Initializing],
};

/** Max `sendMetadata` payloads admitted per conversation (per chatId). */
const METADATA_SESSION_CAP = 1000;
/** Fixed-window rate limit: max payloads per window. */
const METADATA_RATE_LIMIT = 10;
/** Rate-limit window length (ms). */
const METADATA_RATE_WINDOW_MS = 1000;
/** Max payloads buffered before `<ps-pype>` mounts. */
const METADATA_PREBOOT_BUFFER_CAP = 50;

const ALLOWED_COMMANDS: Record<LoaderStatus, LoaderCommand[]> = {
  // `sendMetadata` is allowed in Idle so analytics fired after `createPypeLoader()`
  // but before `boot()` are buffered (flushed at boot tail), not rejected.
  // `reset` is a safe no-op in Idle (already at the reset state) — allowing it
  // keeps `can('reset')` honest with `reset()`, which only rejects mid-boot.
  [LoaderStatus.Idle]: ['boot', 'reset', 'sendMetadata', 'registerForEvents'],
  // `expand` is intentionally allowed during Initializing — boot-time button
  // clicks are a common race that we want to buffer, not reject. Same for
  // `sendMetadata`: analytics commonly fire during boot; we buffer them.
  // `registerForEvents` only attaches listeners (no state dependency), so
  // it's allowed everywhere the Loader can accept commands.
  [LoaderStatus.Initializing]: ['expand', 'sendMetadata', 'registerForEvents'],
  [LoaderStatus.ChatActive]: [
    'shutdown',
    'endChat',
    'update',
    'applyStyles',
    'applyBranding',
    'expand',
    'minimize',
    'toggle',
    'restart',
    'unmount',
    'reset',
    'sendMetadata',
    'registerForEvents',
  ],
  // On a natural/`endChat()` end the widget stays mounted on the chat-ended
  // screen, so the panel ops + `restart` ("Start New") stay valid here — their
  // real effect is further gated by mount/ready/ended (see `can()`). After a
  // `shutdown()` the element is gone, so those degrade to a mounted-guard
  // no-op. `unmount`/`shutdown` detach the still-mounted element; `boot`
  // re-activates from scratch.
  [LoaderStatus.ChatEnded]: [
    'restart',
    'expand',
    'minimize',
    'toggle',
    // Designer-only, and allowed here for the same reason the panel ops are: a
    // soft end leaves the widget MOUNTED on the chat-ended screen, whose theme,
    // tokens and brand surfaces are all still on screen. Without these the Pro
    // Studio designer went dead the moment its preview conversation ended —
    // every later edit a silent no-op behind `can()`. Widening the STATUS gate
    // is safe because `_assertDesignerPreview` is the orthogonal one that keeps
    // a customer embed out, and it still applies.
    'applyStyles',
    'applyBranding',
    // Not designer-only, unlike the two above: the chat-ended screen and the
    // launcher still render copy, so re-languaging them is a real effect, not a
    // convenience. Mount-gated below for the same reason they are.
    'update',
    'shutdown',
    'reset',
    'unmount',
    'boot',
    'registerForEvents',
  ],
  [LoaderStatus.Error]: ['reset', 'unmount', 'boot', 'registerForEvents'],
};

/** Rendering modality — mirrors `AppConfig['modality']`. */
type Modality = NonNullable<AppConfig['modality']>;

/**
 * Commands that are only meaningful in a subset of modalities. A command NOT
 * listed here applies to every modality. Listed commands warn + no-op when
 * called in a modality that isn't in their set (a SECOND, orthogonal axis to
 * the status gate in {@link ALLOWED_COMMANDS}).
 *
 * `expand` / `minimize` / `toggle` drive the floating messenger panel.
 * Fullpage is always-on with no launcher/panel, so they have nothing to act
 * on there. The `window` render target ALSO makes them no-ops (the popup is
 * the panel) — that check lives in `_isApplicableInModality` since it's keyed
 * on `target`, not modality.
 */
const MODALITY_SCOPED_COMMANDS: Partial<Record<LoaderCommand, Modality[]>> = {
  expand: ['floating'],
  minimize: ['floating'],
  toggle: ['floating'],
};

/**
 * Panel-visibility commands that warn + no-op unless the widget is MOUNTED.
 * The orthogonal `shell` region in `<ps-pype>` processes LAUNCH/MINIMIZE in
 * EVERY lifecycle state (connecting, ready, destroyed), so these are NOT gated
 * on `ready` — gating them on `ready` was the bug that made minimize/toggle
 * silently no-op on the chat-ended screen. `expand` is deliberately absent:
 * it buffers when unmounted (boot-race ergonomics) rather than warning.
 */
const MOUNT_ONLY_COMMANDS = new Set<LoaderCommand>([
  'minimize',
  'toggle',
  // The designer gates every live edit on `can()`, so these must report false
  // once `shutdown()` has detached the element — they optional-chain into
  // nothing without it. Needed because they are allowed in `chat_ended` (where
  // a SOFT end leaves the widget mounted and restyling is real); without this
  // the gate would simply have moved the silent no-op from one end to the other.
  'applyStyles',
  'applyBranding',
  'update',
]);

/**
 * Commands that need a live or ended conversation. `restart` drives RESET,
 * wired in both `ready` (mid-chat) and `destroyed` (the chat-ended "Start
 * New" path) — so it's allowed when `ready || ended`. `endChat` drives
 * END_CHAT_REQUESTED, only meaningful while `ready`. `can()` reports `false` for these
 * until `<ps-pype>` reports the right phase (XState-derived), so a host gating
 * UI won't enable a button whose click silently vanishes.
 */
const RUNTIME_READY_COMMANDS = new Set<LoaderCommand>(['endChat']);
// `endChat` additionally requires that no end is already pending — see
// `_isEndAwaitingOutcome`. Not a set, because it is the only command with that
// shape.

const READY_OR_ENDED_COMMANDS = new Set<LoaderCommand>(['restart']);

/**
 * Host-side orchestrator for Pype chat applications.
 *
 * Manages the full lifecycle — configuration, session, DOM mounting,
 * and communication with the `<ps-pype>` web component.
 *
 * @example
 * ```ts
 * import { createPypeLoader } from '@pypestream/pype-loader';
 *
 * const loader = createPypeLoader({
 *   appId: 'app_123',
 *   language: 'en',
 * });
 *
 * loader.on('state:change', (state) => console.log(state.status));
 * await loader.boot();
 * ```
 */
export class PypeLoader {
  private _status: LoaderStatus = LoaderStatus.Idle;
  private _config: AppConfig;
  private _sessionData: {
    accessToken: string;
    chatId: string;
    pypeId: string;
    streamId: string;
    userId: string;
  } | null = null;
  private _error: PypeLoaderError | null = null;
  private _psPype: PypeHostElement | null = null;
  /**
   * Active popup handle when `target: 'window'`. Null for inline. The Loader
   * holds a LIVE reference to the popup's `<ps-pype>` via `_psPype`; this is
   * only the popup mechanics (focus, close, lifecycle watchers).
   */
  private _windowTarget: OpenWindowResult | null = null;
  /**
   * Single-slot buffer for an `expand()` call that happens during boot. The
   * customer's launcher button is typically wired immediately and may be
   * clicked before the Loader reaches `ChatActive`. Buffering one intent and
   * flushing on transition is far better UX than throwing INVALID_STATE.
   *
   * Cleared on any non-ChatActive transition (Error, ChatEnded) and on
   * `shutdown()` / `reset()` / `minimize()` / `toggle()` to prevent stale
   * expands crossing sessions.
   */
  private _pendingOpen = false;
  /**
   * Pre-mount buffer for `sendMetadata` payloads. Covers the window before
   * `<ps-pype>` is mounted (`this._psPype` is null). Flushed at the boot
   * tail — direct to `_psPype.sendMetadata`, bypassing re-admission (these
   * already passed validation + rate-limit + session-cap at buffer time).
   * Bounded by `METADATA_PREBOOT_BUFFER_CAP`.
   */
  private _pendingMetadata: MetadataPayload[] = [];
  /** Per-conversation event count for the session cap. Reset on new chatId. */
  private _metadataSessionCount = 0;
  /** Fixed-window rate-limit state. */
  private _metadataWindowStart = 0;
  private _metadataWindowCount = 0;
  private _metadataRateWarned = false;
  /**
   * Deprecated CustomEvent → sendMetadata bridge. Isolated in its own
   * module so the whole deprecated surface can be deleted mechanically.
   * Constructed lazily on first `registerForEvents`.
   */
  private _eventBridge: EventListenerBridge | null = null;
  /** Last `pype-view-state` we received — drives the event fan-out diff. */
  private _lastViewState: PypeViewState | null = null;
  /**
   * Whether `chat:end-requested` has already fired for the current ask.
   *
   * `endRequested` alone cannot carry this: it matches a `ready`-nested state
   * region that is deliberately not persisted, so any reconnect drops it to
   * false and re-derives it, which would re-fire the event for a single user
   * action. `chat:end` needs no equivalent because its `ChatActive → ChatEnded`
   * status guard is already one-way.
   */
  private _endRequestedEmitted = false;
  private _bootPromise: Promise<void> | null = null;
  private _readyListenerCleanup: (() => void) | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _emitter = mitt<Record<string, any>>();

  private readonly _container: HTMLElement | string | undefined;

  private readonly _onError: ((err: PypeError) => void) | undefined;

  constructor(options: CreatePypeLoaderOptions) {
    if (!options.appId || options.appId.trim() === '') {
      throw new PypeLoaderError(
        LoaderErrorCode.MISSING_APP_ID,
        'appId is required and must be a non-empty string.',
        { loaderStatus: LoaderStatus.Idle }
      );
    }

    try {
      this._config = validateAppConfig(buildAppConfig(options));
    } catch (cause) {
      throw new PypeLoaderError(
        LoaderErrorCode.INVALID_CONFIG,
        'Invalid configuration values.',
        {
          cause: cause instanceof Error ? cause : new Error(String(cause)),
          loaderStatus: LoaderStatus.Idle,
        }
      );
    }

    this._container = options.container;
    this._onError = options.onError;
  }

  /** Current Loader status. */
  get status(): LoaderStatus {
    return this._status;
  }

  /**
   * Return a copy of the current application config.
   *
   * The config is built from {@link CreatePypeLoaderOptions} at construction time
   * and can be updated at runtime via {@link update}.
   */
  getConfig(): AppConfig {
    return this._redactSensitive(this._config);
  }

  /**
   * Shallow-clone a (partial) config with the sensitive `user.token` stripped.
   * Used for anything that leaves the Loader's private state — `getConfig()`
   * and the `update` event payload — so the opaque auth token isn't echoed
   * back to host-page code or analytics listeners.
   */
  private _redactSensitive<T extends Partial<AppConfig>>(config: T): T {
    if (config.user?.token === undefined) return { ...config };
    const { token: _token, ...userRest } = config.user;
    return { ...config, user: userRest };
  }

  /**
   * Restore session, register web component, mount `<ps-pype>` into the DOM,
   * and establish communication.
   *
   * Resolves when the Loader reaches `chat_active`.
   * Rejects with {@link PypeLoaderError} on failure.
   *
   * @param options - Optional boot-time overrides (e.g. container).
   * @throws {PypeLoaderError} `ALREADY_BOOTED` if boot() was already called and is in progress or active.
   * @throws {PypeLoaderError} `MOUNT_FAILED` if web component registration or DOM mounting fails.
   */
  boot(options?: BootOptions): Promise<void> {
    if (this._bootPromise) {
      // Re-boot while a window target is already open just brings it forward
      // (legacy `focusWindow` parity) instead of spawning a second popup.
      if (this._windowTarget && !this._windowTarget.closed) {
        this._windowTarget.focus();
      }
      return this._bootPromise;
    }

    if (
      this._status !== LoaderStatus.Idle &&
      this._status !== LoaderStatus.ChatEnded &&
      this._status !== LoaderStatus.Error
    ) {
      throw new PypeLoaderError(
        LoaderErrorCode.ALREADY_BOOTED,
        `boot() called in invalid state: ${this._status}. Expected idle, chat_ended, or error.`,
        { loaderStatus: this._status }
      );
    }

    this._bootPromise = this._boot(options);
    // Mark the promise as handled even if the consumer never awaits boot().
    // Errors are already surfaced via onError + 'error' event + console;
    // this no-op .catch() exists only to prevent a window-level
    // 'unhandledrejection' from firing on the host page if the consumer
    // ignored the returned promise. Awaiting consumers still see the
    // rejection through their own try/catch or .catch().
    this._bootPromise.catch(() => {});
    return this._bootPromise;
  }

  /**
   * End the current chat session and clear session cookie.
   * Unmounts `<ps-pype>` from DOM. Transitions to `chat_ended`.
   *
   * @throws {PypeLoaderError} `INVALID_STATE` if called before boot() or after reset().
   */
  shutdown(): void {
    this._assertCommand('shutdown');

    this._unmountElement();
    clearStoredSession(this._config.appId!);
    this._sessionData = null;
    this._clearPendingIntents();
    this._lastViewState = null;
    this._endRequestedEmitted = false;

    // From `chat_active` this ends the conversation; from `chat_ended` (a
    // natural/`endChat()` end where the widget was still mounted) the
    // conversation is already ended, so just detach — `chat:end` already
    // fired on that edge, and `ChatEnded → ChatEnded` isn't a valid transition.
    if (this._status !== LoaderStatus.ChatEnded) {
      this._transition(LoaderStatus.ChatEnded);
      this._emitSafe('chat:end', undefined as never);
    }
    this._emitSafe('unmount', undefined as never);
    this._bootPromise = null;
  }

  /**
   * Reset Loader to `idle`, clearing config, session, mounted elements, and errors.
   * Unmounts `<ps-pype>` if mounted. Clears session cookie.
   * After reset, the Loader can be re-booted.
   */
  reset(): void {
    this._assertCommand('reset');

    this._unmountElement();
    clearStoredSession(this._config.appId!);

    this._sessionData = null;
    this._error = null;
    this._bootPromise = null;
    this._clearPendingIntents();
    this._lastViewState = null;
    this._endRequestedEmitted = false;

    this._status = LoaderStatus.Idle;
    this._emitStateChange();
  }

  /**
   * Merge non-visual config (language, branding text) into the active config
   * and emit the `'update'` event. Effective while the widget is mounted — in
   * `chat_active`, and in `chat_ended`, where a soft end leaves the chat-ended
   * screen and the launcher on display still rendering copy.
   *
   * Scope is intentionally narrow — see {@link RuntimeUpdatableConfig}:
   * - **Visual/theme changes** are delivered by the saved design fetched at
   *   boot, not here.
   * - **Session/boot-only fields** (`modality`, `user`, `passthrough`, …) are
   *   fixed for the session; set them at {@link createPypeLoader}.
   *
   * @remarks Only `language` propagates to the running widget. The other fields are merged into the
   * stored config and emitted on `'update'`, but the widget does not re-read them — `config` is
   * boot-immutable, and `restart()` preserves them rather than re-applying them, so they take effect
   * only on a fresh mount. (This previously claimed they applied to the next session; they did not.)
   * Visual/theme changes are owned by the saved design edited in the Pype designer, not this method.
   *
   * @remarks `can('update')` is mount-gated, so it reports `false` once the widget
   * has been detached (`unmount()`, or after `shutdown()`) even though the status
   * still permits the command. Calling it anyway does not throw: the settings are
   * merged into the stored config and take effect on the next boot. Gate host UI
   * on `can('update')` when the intent is to change the RUNNING widget.
   *
   * @throws {PypeLoaderError} `INVALID_STATE` if not in `chat_active` or `chat_ended`.
   */
  update(settings: RuntimeUpdatableConfig): void {
    this._assertCommand('update');

    this._config = { ...this._config, ...settings };
    if (this._psPype) {
      this._psPype.config = this._widgetConfig(this._config);
      // The one field with a live path. Routed through the widget's `@internal` method rather than
      // the config setter, which is deliberately inert.
      if (settings.language !== undefined) {
        this._psPype.setLanguage?.(settings.language);
      }
    }
    this._emitSafe('update', settings);
  }

  /**
   * Apply style overrides (theme, tokens, part overrides, raw CSS) to the
   * mounted Pype widget. Styles flow through XState for state management
   * and are applied to both launcher and messenger shadow roots.
   *
   * Only effective when status is `chat_active`.
   * Emits `'applyStyles'` event with the provided overrides.
   *
   * @internal Designer-preview only — used by the Pro Studio pype-designer to
   * push unsaved, in-progress style edits. **Requires
   * {@link CreatePypeLoaderOptions.skipDesignFetch}**, which is now enforced
   * rather than assumed: this is a full snapshot, so a live embed calling it
   * would replace the saved design wholesale and race the boot fetch.
   * Not part of the public embed surface.
   *
   * @throws {PypeLoaderError} `INVALID_STATE` if not in `chat_active` or `chat_ended`.
   * @throws {PypeLoaderError} `INVALID_CONFIG` without `skipDesignFetch: true`.
   */
  applyStyles(styles: StyleOverrides): void {
    this._assertCommand('applyStyles');
    this._assertDesignerPreview('applyStyles');

    this._psPype?.applyStyles?.(styles);
    this._emitSafe('applyStyles', styles);
  }

  /**
   * Apply branding overrides (name, logo, launcher text, brand-moment image,
   * hide-branding) to the mounted widget. Branding flows through XState
   * (`BRANDING_UPDATE`) and reactive store getters update the launcher, header,
   * and brand surfaces live. Symmetric to {@link applyStyles}; a FULL snapshot,
   * not a patch.
   *
   * @internal Designer-preview only — live/customer embeds get branding from
   * the saved design at boot. **Requires `skipDesignFetch: true`**, enforced.
   * @throws {PypeLoaderError} `INVALID_STATE` if not in `chat_active` or `chat_ended`.
   * @throws {PypeLoaderError} `INVALID_CONFIG` without `skipDesignFetch: true`.
   */
  applyBranding(branding: BrandingOverrides): void {
    this._assertCommand('applyBranding');
    this._assertDesignerPreview('applyBranding');

    this._psPype?.applyBranding?.(branding);
    this._emitSafe('applyBranding', branding);
  }

  /**
   * Expand the messenger panel (floating modality only).
   *
   * - In `chat_active` / `chat_ended` (widget mounted): dispatches immediately
   *   via `<ps-pype>`. The shell region acts on it in every lifecycle state,
   *   so it also expands the chat-ended screen.
   * - In `initializing` (boot race window): buffers a single pending intent
   *   and flushes it on the transition to `chat_active`. This is the
   *   expected ergonomics for host-page launcher buttons wired before
   *   `boot()` resolves.
   * - In any other state (`idle`, `error`): throws `INVALID_STATE`.
   *
   * Renamed from the former `open()` — clearer that it expands the panel
   * rather than ending anything (`minimize` is the inverse, `endChat` ends
   * the conversation).
   *
   * **Not applicable in `modality: 'fullpage'` or `target: 'window'`** — both
   * are always-on surfaces with no launcher/panel to expand. A call there is a
   * `console.warn` + no-op (never throws); `can('expand')` returns `false`.
   *
   * @throws {PypeLoaderError} `INVALID_STATE` if called before `boot()` or
   *   after errored / reset.
   */
  expand(): void {
    if (!this._guardModality('expand')) return;
    this._assertCommand('expand');
    // Dispatch immediately: the shell region expands the panel now in any
    // lifecycle state, and a not-yet-mounted runtime simply swallows the
    // LAUNCH (harmless). Dispatching unconditionally keeps `expand()` correct
    // even if mount *reporting* lags — important because in headless mode
    // `expand()` is the ONLY way to surface the messenger.
    this._psPype?.openMessenger?.();
    // If the widget isn't mounted yet (boot race), buffer the intent so the
    // `pype-chat-ready` edge re-issues it (see onChatReady).
    if (!this._isMounted()) {
      this._pendingOpen = true;
    }
  }

  /**
   * Minimize the messenger panel back to the launcher (floating modality
   * only). Works whenever the widget is mounted — including on the chat-ended
   * screen — since the shell region handles MINIMIZE in every lifecycle state.
   * Clears any pending expand intent so a buffered `expand()` from before boot
   * completed can't override the customer's explicit minimize.
   *
   * Renamed from the former `close()` — `close` read like "end the chat";
   * `minimize` is unambiguous panel mechanics.
   *
   * **Not applicable in `modality: 'fullpage'` or `target: 'window'`** — no
   * panel to collapse (in a popup the OS window controls minimize/close). A
   * call there is a `console.warn` + no-op; `can('minimize')` returns `false`.
   *
   * @throws {PypeLoaderError} `INVALID_STATE` if not in `chat_active` /
   *   `chat_ended`.
   */
  minimize(): void {
    if (!this._guardModality('minimize')) return;
    this._assertCommand('minimize');
    if (!this._guardMounted('minimize')) return;
    this._pendingOpen = false;
    this._psPype?.closeMessenger?.();
  }

  /**
   * Toggle the messenger panel between expanded and minimized based on current
   * view state (floating modality only). Works whenever the widget is mounted.
   *
   * **Not applicable in `modality: 'fullpage'` or `target: 'window'`** — no
   * panel to toggle. A call there is a `console.warn` + no-op;
   * `can('toggle')` returns `false`.
   *
   * @throws {PypeLoaderError} `INVALID_STATE` if not in `chat_active` /
   *   `chat_ended`.
   */
  toggle(): void {
    if (!this._guardModality('toggle')) return;
    this._assertCommand('toggle');
    if (!this._guardMounted('toggle')) return;
    this._pendingOpen = false;
    this._psPype?.toggleMessenger?.();
  }

  /**
   * End the current conversation while keeping the widget mounted on the
   * chat-ended screen ("soft" end). Contrast with {@link shutdown}, which also
   * clears the session and removes the element from the page.
   *
   * Drives the machine `END_CHAT_REQUESTED` through `<ps-pype>` — the same path
   * as the in-widget end-chat button. **This is a request, not a completed
   * end:** the backend may keep the conversation alive (a configured survey or
   * end-chat-node route answers with more messages instead of ending), and the
   * widget stays interactive until it confirms.
   *
   * The end surfaces via the `ended` view-state once the backend confirms,
   * which the Loader turns into a `chat:end` event + a move to `chat_ended`
   * (see `_handleViewState`). That can therefore arrive well after this call —
   * do not treat `chat:end` as an acknowledgement of it. From there `restart()`
   * starts a fresh conversation.
   *
   * Effective only while the conversation is `ready`; before ready or after
   * it has already ended it's a warn + no-op.
   *
   * @throws {PypeLoaderError} `INVALID_STATE` if called before boot() or after
   *   errored / reset.
   */
  endChat(): void {
    this._assertCommand('endChat');
    if (!this._guardReady('endChat')) return;
    // The machine drops a duplicate request, so warn rather than let the call
    // vanish — a host retrying because "nothing happened" is the likely reason
    // it got here, and the honest answer is that the first ask is still open.
    if (this._isEndAwaitingOutcome()) {
      console.warn(
        '[Pype] `endChat()` was ignored — an end is already awaiting the backend. Once it answers (a survey or exit-node route may continue the conversation), calling this again asks to end that too.'
      );
      return;
    }
    this._psPype?.endChat?.();
  }

  /**
   * Restart the conversation: tear down the current conversation and create a
   * fresh session, keeping the widget mounted. Legacy parity for `restartChat`.
   *
   * Effective while the conversation is **active** (`ready`) OR has **ended**
   * (the chat-ended screen) — `RESET` is wired in both phases. Before the
   * runtime is ready (connecting) it's a warn + no-op, since `RESET` would be
   * swallowed. Restarting from the ended screen re-activates the Loader
   * (`chat_ended → chat_active`); the fresh session surfaces via `chat:start`
   * once `<ps-pype>` reports it.
   *
   * Drives the machine `RESET` through `<ps-pype>`'s intent-named
   * `restartConversation()` — the Loader never sees the XState event name.
   * Emits `'chat:restart'`.
   *
   * @param options.clearHistory - When `true`, drop the prior transcript
   *   instead of archiving it into the new conversation. Defaults to `false`
   *   (the prior messages are retained, separated by a divider).
   * @throws {PypeLoaderError} `INVALID_STATE` if called before boot() or after
   *   errored / reset.
   */
  restart(options?: { clearHistory?: boolean }): void {
    this._assertCommand('restart');
    // RESET is handled in `ready` (mid-chat) and `destroyed` (ended); in any
    // other phase it's swallowed. Warn + no-op rather than silently drop it.
    if (!this._guardRestartable('restart')) return;
    // A fresh conversation re-initialises view state; drop any buffered expand
    // intent and force the next view-state to be treated as an initial sync.
    this._pendingOpen = false;
    this._lastViewState = null;
    this._endRequestedEmitted = false;
    this._psPype?.restartConversation?.(options);
    this._emitSafe('chat:restart', undefined as never);
  }

  /**
   * Whether the Loader currently holds a chat session — a restored session
   * from localStorage at boot, or one created during this run. Legacy parity
   * for `isVisible()` (which probed localStorage to decide whether to auto-boot
   * a returning user). Loader-local — does not query `<ps-pype>` or expose any
   * session internals.
   *
   * Note: returns `true` for a restored session the backend may later reject
   * (e.g. a stale token that 403s on connect), at which point the session is
   * cleared and this returns `false`.
   */
  hasSession(): boolean {
    return this._sessionData !== null;
  }

  /**
   * Send a host-page event into the active conversation. The payload is
   * persisted by the backend and surfaced to the bot on its next request
   * as `metadata.events` (a timestamped wrapper around your `data`).
   *
   * Use this to push page views, cart changes, auth signals, etc. into a
   * solution for routing, personalization, or analytics. This is the
   * recommended API. To tap a parent-page CustomEvent stream from code
   * that doesn't hold `loader`, add a one-line listener that forwards into
   * this method (see the README "Sending host-page events" recipe).
   *
   * Buffering: calling after `createPypeLoader()` but before the chat is
   * connected is safe — the payload is queued internally and flushed
   * automatically once the session is ready.
   *
   * @remarks
   * Pre-load limitation (this release): `sendMetadata` requires the `loader`
   * instance, which exists only after `createPypeLoader()`. Events your page
   * fires *before the Loader script itself loads* cannot be captured in this
   * version. If your integration needs to capture or queue events before
   * the Loader is available on the page, **contact the Pypestream team** —
   * first-class pre-load buffering is not built yet, and we prioritize it
   * based on concrete customer need, so your use case directly informs
   * whether and when it ships.
   *
   * @param data - A JSON object or array (not a primitive). Deeply nested
   *   (>5 levels) or oversized (>32KB stringified) payloads are rejected.
   * @throws {PypeLoaderError} `INVALID_STATE` if called after the chat ended
   *   or errored.
   * @throws {PypeLoaderError} `INVALID_METADATA` if `data` is not an
   *   object/array or violates the depth/size caps.
   */
  sendMetadata(data: MetadataPayload): void {
    this._assertCommand('sendMetadata');
    this._enqueueMetadata(data, { onInvalid: 'throw' });
  }

  /**
   * @deprecated Prefer {@link sendMetadata}. `registerForEvents` is thin
   * sugar over a one-line listener
   * (`addEventListener(name, e => loader.sendMetadata(e.detail))`) and is
   * retained only to ease migration from Pype 1.0. It will be removed in a
   * future major version. New integrations should use `sendMetadata` plus
   * the listener recipe in the README.
   *
   * Bridge a parent-page CustomEvent stream into the conversation:
   * subscribes to the named CustomEvents on `eventTarget` and forwards each
   * event's `detail` into the bot's `metadata.events` via the same path as
   * {@link sendMetadata}. Useful when event producers (GTM tags,
   * third-party scripts) don't hold the `loader` reference — call it once from
   * code that does; producers only need `window.dispatchEvent(...)`.
   *
   * Unlike `sendMetadata`, invalid payloads and rate/session-cap overflows
   * on this path are dropped with a console warning and never throw — a
   * misbehaving page event must never break the host page.
   *
   * Events fired before the Loader script loads are not captured — see the
   * `sendMetadata` pre-load note.
   *
   * @param eventNames - CustomEvent names to listen for.
   * @param eventTarget - EventTarget to subscribe on. Defaults to `window`.
   */
  registerForEvents(eventNames: string[], eventTarget?: EventTarget): void {
    this._assertCommand('registerForEvents');
    if (!this._eventBridge) {
      // Closure sink — the bridge holds a `(detail) => void`, never an
      // `loader` reference. This is what keeps the deprecated layer cleanly
      // deletable (see register-for-events.ts header).
      this._eventBridge = new EventListenerBridge((detail) =>
        this._enqueueMetadata(detail, { onInvalid: 'warn' })
      );
    }
    this._eventBridge.register(eventNames, eventTarget);
  }

  /**
   * Shared admission + buffer-or-forward path for both `sendMetadata`
   * (onInvalid: 'throw') and the deprecated bridge (onInvalid: 'warn').
   *
   * Order: validate → (forward path only) rate-limit → session-cap →
   * forward-or-buffer. The **rate limit gates live forwarding only**, not
   * buffering — a pre-boot burst is bounded by the buffer cap, not the
   * rate window, so 50 buffered events all survive to the boot-tail flush
   * (which bypasses admission entirely). Session-cap + quota are consumed
   * only when an event actually forwards or buffers (a rate/buffer drop
   * consumes nothing).
   */
  private _enqueueMetadata(
    data: unknown,
    opts: { onInvalid: 'throw' | 'warn' }
  ): void {
    // 1. Validate
    const result = validateMetadata(data);
    if (!result.ok) {
      const msg = `sendMetadata rejected: payload is ${result.reason}.`;
      if (opts.onInvalid === 'throw') {
        throw new PypeLoaderError(LoaderErrorCode.INVALID_METADATA, msg, {
          loaderStatus: this._status,
        });
      }
      console.warn(`[Pype] ${msg}`);
      return;
    }
    // Validated: `data` is an object or array.
    const payload = data as MetadataPayload;
    const willForward = !!this._psPype;

    // 2. Rate limit (fixed window) — gates LIVE forwarding only; buffering
    //    is bounded by the buffer cap instead. Warn throttled to 1/window.
    if (willForward && this._isRateLimited()) {
      return;
    }

    // 3. Session cap (per conversation) — applies to anything destined for
    //    the backend (forward now, or buffer→flush later). Warn once.
    if (this._metadataSessionCount >= METADATA_SESSION_CAP) {
      if (this._metadataSessionCount === METADATA_SESSION_CAP) {
        this._metadataSessionCount += 1; // bump so this branch warns once
        console.warn(
          `[Pype] sendMetadata session cap reached (${METADATA_SESSION_CAP} events for this conversation). Further events are ignored until a new conversation starts.`
        );
      }
      return;
    }

    // 4. Forward or buffer (quota consumed only here).
    if (willForward) {
      this._metadataSessionCount += 1;
      this._psPype!.sendMetadata?.(payload);
      return;
    }
    if (this._pendingMetadata.length >= METADATA_PREBOOT_BUFFER_CAP) {
      console.warn(
        `[Pype] sendMetadata pre-boot buffer full (${METADATA_PREBOOT_BUFFER_CAP}); event dropped. Call boot() earlier or reduce pre-boot events.`
      );
      return;
    }
    this._metadataSessionCount += 1;
    this._pendingMetadata.push(payload);
  }

  /**
   * Fixed-window rate-limit check for the live-forward path. Returns true
   * (drop) when the current window is saturated; warns once per window.
   */
  private _isRateLimited(): boolean {
    const now = Date.now();
    if (now - this._metadataWindowStart >= METADATA_RATE_WINDOW_MS) {
      this._metadataWindowStart = now;
      this._metadataWindowCount = 0;
      this._metadataRateWarned = false;
    }
    if (this._metadataWindowCount >= METADATA_RATE_LIMIT) {
      if (!this._metadataRateWarned) {
        this._metadataRateWarned = true;
        console.warn(
          `[Pype] sendMetadata rate limit exceeded (${METADATA_RATE_LIMIT}/s); events dropped. Throttle your event dispatch.`
        );
      }
      return true;
    }
    this._metadataWindowCount += 1;
    return false;
  }

  /**
   * Flush buffered pre-boot metadata straight to `<ps-pype>` — NO
   * re-admission (validation/rate/session-cap already applied at buffer
   * time; re-running rate-limit here would drop legitimately-buffered
   * events at boot). Mirrors `_pendingOpen`'s no-recheck flush.
   */
  private _flushPendingMetadata(): void {
    if (!this._psPype || this._pendingMetadata.length === 0) {
      return;
    }
    const pending = this._pendingMetadata;
    this._pendingMetadata = [];
    for (const data of pending) {
      this._psPype.sendMetadata(data);
    }
  }

  /**
   * Read the current messenger open/closed state.
   *
   * Returns `false` before the runtime has started (idle, initializing).
   * Useful for syncing a custom launcher button's open/close icon.
   */
  get isOpen(): boolean {
    // Window target: a closed popup is never "open", regardless of modality.
    if (this._config.target === 'window') {
      if (!this._windowTarget || this._windowTarget.closed) return false;
    }
    // Fullpage is always-on — no launcher, no panel to open/close. It reports
    // "open" whenever the chat is active, rather than reading the view-state
    // (which is false during fullpage boot before the messenger renders).
    if (this._resolvedModality() === 'fullpage') {
      return this._status === LoaderStatus.ChatActive;
    }
    return this._readViewState()?.isOpen ?? false;
  }

  /**
   * Read the current launcher-driving state — busy/loading flag and error
   * flag. Customer-owned launcher buttons read
   * this for initial render; subscribe to `launcher-state:change` and
   * the `state:change` event for live updates.
   *
   * Returns safe defaults when the widget is not yet booted.
   */
  getLauncherState(): {
    busy: boolean;
    hasError: boolean;
  } {
    const view = this._readViewState();
    return {
      busy: view?.busy ?? false,
      hasError: view?.hasError ?? false,
    };
  }

  /**
   * Unmount `<ps-pype>` from the DOM without ending the session.
   * Session cookie is preserved — a subsequent boot() will restore it.
   * Emits `'unmount'` event.
   */
  unmount(): void {
    this._assertCommand('unmount');
    this._unmountElement();
    this._emitSafe('unmount', undefined as never);
  }

  /**
   * Check whether the Loader can accept a given command in its current state.
   *
   * @example
   * ```ts
   * if (loader.can('shutdown')) {
   *   loader.shutdown();
   * }
   * ```
   */
  can(command: LoaderCommand): boolean {
    if (!ALLOWED_COMMANDS[this._status].includes(command)) return false;
    if (!this._isApplicableInModality(command)) return false;
    // Panel ops need only a mounted widget; restart/endChat need the right
    // conversation phase. All readiness is read live from `<ps-pype>`
    // (XState-derived — single source of truth), never a loader-side mirror,
    // so a host gating UI on `can()` won't enable a button whose click vanishes.
    if (MOUNT_ONLY_COMMANDS.has(command) && !this._isMounted()) return false;
    if (RUNTIME_READY_COMMANDS.has(command) && !this._isReady()) return false;
    if (command === 'endChat' && this._isEndAwaitingOutcome()) return false;
    if (
      READY_OR_ENDED_COMMANDS.has(command) &&
      !(this._isReady() || this._isEnded())
    ) {
      return false;
    }
    return true;
  }

  /** Whether `<ps-pype>` is mounted — the gate for panel ops. */
  private _isMounted(): boolean {
    return this._psPype !== null;
  }

  /**
   * Read the widget's view-state safely. In the `window` target `_psPype` lives
   * in the POPUP's realm; once that popup is closed — including the up-to-
   * `CLOSE_POLL_MS` gap before the close-poll fires `_handleWindowClosed` — the
   * cross-window reference points into a discarded browsing context, where a
   * `getViewState()` call can throw. `can()` / `getLauncherState()` / `isOpen`
   * are the host-safe SYNCHRONOUS surface and must never throw into host code,
   * so return `null` on a closed popup or any access error.
   */
  /**
   * The widget's booted modality first: with no `modality` in the snippet, a
   * fullpage design is visible to the Loader only through the widget.
   */
  private _resolvedModality(): Modality {
    return (
      this._readViewState()?.modality ?? this._config.modality ?? 'floating'
    );
  }

  private _readViewState(): PypeViewState | null {
    if (
      this._config.target === 'window' &&
      (!this._windowTarget || this._windowTarget.closed)
    ) {
      return null;
    }
    try {
      return this._psPype?.getViewState?.() ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Whether the widget runtime has reached `ready` (and will act on
   * restart/endChat). Read on-demand from `<ps-pype>.getViewState()`, which
   * derives it from XState — no cached flag, no second source of truth.
   */
  private _isReady(): boolean {
    return this._readViewState()?.ready ?? false;
  }

  /** Whether the conversation has ended while the widget stays mounted. */
  private _isEnded(): boolean {
    return this._readViewState()?.ended ?? false;
  }

  /**
   * Whether an end is asked for and still UNANSWERED. `endChat` is a no-op then,
   * so `can()` must say so rather than leave a host's button enabled for a command
   * that does nothing. Deliberately does NOT cover a declined end that is running:
   * asking again there re-pushes, which is how a host escapes a survey.
   */
  private _isEndAwaitingOutcome(): boolean {
    return this._readViewState()?.endAwaitingOutcome ?? false;
  }

  /**
   * Warn + return `false` when a panel op is called with no widget mounted.
   * Never throws — drop-in callers must not crash.
   */
  private _guardMounted(command: LoaderCommand): boolean {
    if (this._isMounted()) return true;
    console.warn(`[Pype] \`${command}()\` was ignored — no widget is mounted.`);
    return false;
  }

  /**
   * Warn + return `false` when a runtime-dependent command is called before
   * the runtime is `ready` (the event would be silently swallowed). Returns
   * `true` when ready (caller proceeds). Like `_guardModality`, never throws.
   */
  private _guardReady(command: LoaderCommand): boolean {
    if (this._isReady()) return true;
    console.warn(
      `[Pype] \`${command}()\` was ignored — the chat runtime isn't ready (still connecting, or the conversation has ended).`
    );
    return false;
  }

  /**
   * Warn + return `false` when `restart` is called outside the phases that
   * handle RESET (`ready` mid-chat, or `ended` on the chat-ended screen).
   * Never throws.
   */
  private _guardRestartable(command: LoaderCommand): boolean {
    if (this._isReady() || this._isEnded()) return true;
    console.warn(
      `[Pype] \`${command}()\` was ignored — the chat runtime isn't ready or ended yet (still connecting).`
    );
    return false;
  }

  /**
   * Applicability check for panel commands (orthogonal to the status gate).
   * Returns `true` for commands that apply everywhere, or for panel-scoped
   * commands (`expand`/`minimize`/`toggle`) when the current modality AND
   * render target can act on them.
   *
   * Panel commands are no-ops in two cases: `fullpage` modality (no
   * launcher/panel to open) and the `window` render target (the popup window
   * IS the surface — it's always shown, and the OS window controls handle
   * minimize/close). Both are SOFT failures — a `console.warn` + no-op via
   * `_guardModality`, never a throw — so drop-in callers that blindly call
   * `toggle()` don't crash the host page.
   */
  private _isApplicableInModality(command: LoaderCommand): boolean {
    const scoped = MODALITY_SCOPED_COMMANDS[command];
    if (!scoped) return true;
    // Window render target: no panel to expand/minimize/toggle.
    if (this._config.target === 'window') return false;
    const modality = this._resolvedModality();
    return scoped.includes(modality);
  }

  /**
   * Warn (once-per-call) that a panel command was a no-op in the current
   * modality / render target, and return `false` so the command method can
   * bail. Returns `true` when the command IS applicable (caller proceeds).
   */
  private _guardModality(command: LoaderCommand): boolean {
    if (this._isApplicableInModality(command)) return true;
    if (this._config.target === 'window') {
      console.warn(
        `[Pype] \`${command}()\` is not applicable in the \`window\` render target (the popup window is the panel) and was ignored.`
      );
    } else {
      const modality = this._resolvedModality();
      console.warn(
        `[Pype] \`${command}()\` is not applicable in \`${modality}\` modality and was ignored.`
      );
    }
    return false;
  }

  /**
   * Subscribe to an Loader event.
   *
   * @returns An unsubscribe function for convenience.
   *
   * @example
   * ```ts
   * const off = loader.on('chat:start', ({ conversationId }) => {
   *   console.log('Chat started:', conversationId);
   * });
   * // Later: off();
   * ```
   */
  on<E extends keyof LoaderEvents>(
    event: E,
    cb: (payload: LoaderEvents[E]) => void
  ): () => void {
    this._emitter.on(event, cb);
    return () => this._emitter.off(event, cb);
  }

  /** Unsubscribe from an Loader event. */
  off<E extends keyof LoaderEvents>(
    event: E,
    cb: (payload: LoaderEvents[E]) => void
  ): void {
    this._emitter.off(event, cb);
  }

  /**
   * Return a snapshot of the current Loader state.
   * This is a plain object — not a live reference.
   */
  getState(): LoaderState {
    switch (this._status) {
      case LoaderStatus.Idle:
        return { status: 'idle' };
      case LoaderStatus.Initializing:
        return { status: 'initializing' };
      case LoaderStatus.ChatActive:
        return {
          status: 'chat_active',
          conversationId: this._sessionData?.chatId ?? '',
          session: this._toSessionInfo(),
        };
      case LoaderStatus.ChatEnded:
        return {
          status: 'chat_ended',
          session: this._toSessionInfo(),
        };
      case LoaderStatus.Error:
        return {
          status: 'error',
          error:
            this._error ??
            new PypeLoaderError(LoaderErrorCode.UNKNOWN, 'Unknown error'),
        };
    }
  }

  private async _boot(options?: BootOptions): Promise<void> {
    const isWindow = this._config.target === 'window';
    // Distinguishes "popup opened, then torn down mid-boot" (a benign teardown
    // that already managed state — see the catch) from "popup never opened /
    // was blocked" (a genuine boot failure → Error).
    let windowOpened = false;
    try {
      this._transition(LoaderStatus.Initializing);

      const config = this._config;

      // Window target: open the popup NOW — still synchronous within the
      // caller's user gesture (nothing above awaited), or the browser blocks
      // it. `openPypeWindow` may throw `WINDOW_BLOCKED` (unresolvable runtime
      // URL); a blocked popup returns null.
      if (isWindow) {
        const wt = openPypeWindow(config);
        if (!wt) {
          throw new PypeLoaderError(
            LoaderErrorCode.WINDOW_BLOCKED,
            "target: 'window' popup was blocked. Call boot() from a user " +
              'gesture (e.g. a click handler) so the browser allows the popup.',
            { loaderStatus: this._status }
          );
        }
        this._windowTarget = wt;
        windowOpened = true;
        this._emitSafe('window:open', undefined as never);
        wt.onClose(() => this._handleWindowClosed());
        wt.onReload(() => this._handleWindowReload());
      }

      const storedSession = getStoredSession(config.appId!);
      if (storedSession) {
        this._sessionData = {
          accessToken: storedSession.accessToken,
          chatId: storedSession.chatId,
          pypeId: storedSession.pypeId,
          streamId: storedSession.streamId,
          userId: storedSession.userId,
        };
      }

      // Session creation is owned by <ps-pype>/XState; we only restore from localStorage.
      this._transition(LoaderStatus.ChatActive);

      let container: HTMLElement | null;
      if (isWindow) {
        // Wait for the popup to import the runtime + define `ps-pype` in its
        // own realm; then mount into the popup body (its ownerDocument drives
        // element creation — see `_mountElement`).
        await this._windowTarget!.ready;
        if (this._windowTarget!.closed) {
          throw new PypeLoaderError(
            LoaderErrorCode.WINDOW_BLOCKED,
            'The popup was closed before the widget finished mounting.',
            { loaderStatus: this._status }
          );
        }
        container = this._windowTarget!.window.document.body;
      } else {
        container = resolveContainer(
          options?.container ?? this._container,
          this._status
        );
      }

      // When the window branch adopted an already-running popup, re-wire the
      // live `<ps-pype>` without re-initializing it (that would restart the chat).
      const adopt = isWindow && this._windowTarget?.adopted === true;
      if (container) {
        try {
          await this._mountElement(
            container,
            storedSession,
            adopt ? { adopt: true } : undefined
          );
        } catch (cause) {
          throw new PypeLoaderError(
            LoaderErrorCode.MOUNT_FAILED,
            'Failed to mount <ps-pype> web component.',
            {
              cause: cause instanceof Error ? cause : new Error(String(cause)),
              loaderStatus: LoaderStatus.ChatActive,
            }
          );
        }
      }

      // Stored session: emit chat:start now. Fresh session: chat:start fires on pype-session-created.
      if (storedSession) {
        this._emitSafe('chat:start', {
          conversationId: storedSession.chatId,
        });
      }
      if (container) {
        this._emitSafe('mount', undefined as never);
      }

      // Arm reload detection now that a live `<ps-pype>` exists in the popup
      // (no-op for inline — `_windowTarget` is null).
      this._windowTarget?.notifyMounted();

      // Window target: the widget auto-surfaces its own shell on mount (the
      // floating/fullpage modality sends LAUNCH itself for `target: 'window'`),
      // so the Loader does NOT drive a panel op here — `expand`/`minimize`/
      // `toggle` are intentionally no-ops in window mode.

      // _pendingOpen is NOT flushed here — LAUNCH would be swallowed before `ready`.
      // Flushed on the `pype-chat-ready` edge in `_mountElement` instead.
      this._flushPendingMetadata();
    } catch (err) {
      // Window teardown during the popup handshake: a user close
      // (`_handleWindowClosed`) or `shutdown()`/`reset()` (`_unmountElement`)
      // rejects the `ready` we were awaiting AND nulls `_windowTarget`. That
      // path already settled Loader state (chat_ended / idle), emitted
      // `window:close`, and closed the popup — so don't stomp it with an Error
      // transition or a duplicate teardown. Just settle boot()'s promise as a
      // rejection the caller can catch. (`windowOpened` excludes the
      // never-opened / popup-blocked case, which IS a genuine Error below.)
      if (isWindow && windowOpened && !this._windowTarget) {
        this._bootPromise = null;
        throw err instanceof PypeLoaderError
          ? err
          : new PypeLoaderError(
              LoaderErrorCode.WINDOW_BLOCKED,
              err instanceof Error ? err.message : String(err),
              { cause: err instanceof Error ? err : undefined }
            );
      }

      const sdkError =
        err instanceof PypeLoaderError
          ? err
          : new PypeLoaderError(LoaderErrorCode.UNKNOWN, String(err), {
              cause: err instanceof Error ? err : undefined,
            });

      this._error = sdkError;
      // Window target: a boot failure means the popup (if any) is orphaned —
      // close it so a blank/half-booted window isn't left on screen, and emit
      // `window:close` to pair the `window:open` fired when it opened, so a
      // host tracking the pair sees a matched close on boot failure too.
      if (isWindow && this._windowTarget) {
        const wt = this._windowTarget;
        this._windowTarget = null;
        this._emitSafe('window:close', undefined as never);
        try {
          wt.close();
        } catch {
          /* best-effort */
        }
      }
      // Status is always Initializing or ChatActive here — both allow → Error.
      // Routing through _transition() ensures _clearPendingIntents() runs so a
      // buffered open() can't leak into the next session.
      this._transition(LoaderStatus.Error);
      this._reportError(sdkError);
      this._bootPromise = null;
      throw sdkError;
    }
  }

  private async _mountElement(
    container: HTMLElement,
    storedSession: StoredSessionData | null,
    opts?: { allowEnded?: boolean; adopt?: boolean }
  ): Promise<void> {
    const config = this._config;
    // Window target: `ps-pype` is defined in the POPUP's realm by its
    // bootstrap import — registering it in the opener realm would be useless
    // (a separate registry) and pointlessly load the runtime twice. Ensure the
    // popup registry has upgraded the tag before we create it (the `ready`
    // handshake already implies the import ran, so this is usually instant).
    const doc = container.ownerDocument ?? document;
    if (config.target === 'window') {
      const registry =
        this._windowTarget?.window?.customElements ??
        doc.defaultView?.customElements;
      if (registry && !registry.get('ps-pype') && registry.whenDefined) {
        await registry.whenDefined('ps-pype');
      }
    } else {
      await registerPypeWC();
    }

    // Race-safety: between `registerPypeWC()` resolving and us continuing,
    // the Loader may have been shut down or reset (host re-rendered, modality
    // changed, container swapped). Without this guard the in-flight mount
    // resumes after teardown, creates a fresh `<ps-pype>` with the stale
    // config, and the new Loader instance then either finds and reuses it
    // (sticking us on the previous modality) or coexists with it (two
    // ps-pype elements). Bail before touching the DOM.
    //
    // `allowEnded` opts out of the ChatEnded bail for the window reload-restore
    // path: refreshing an ended-chat popup must re-mount the widget (so it shows
    // the chat-ended screen again) rather than bail and leave a blank popup.
    // Idle/Error still bail (real teardown), and a vanished popup always bails.
    if (
      this._status === LoaderStatus.Idle ||
      this._status === LoaderStatus.Error ||
      (this._status === LoaderStatus.ChatEnded && !opts?.allowEnded) ||
      (config.target === 'window' &&
        (!this._windowTarget || this._windowTarget.closed))
    ) {
      return;
    }

    let psPype: PypeHostElement | null = null;
    let createdNew = false;

    try {
      psPype = container.querySelector('ps-pype') as PypeHostElement | null;
      if (!psPype) {
        // Create in the CONTAINER's document (the popup doc for window mode,
        // the host doc for inline) so the element upgrades in the right realm.
        psPype = doc.createElement('ps-pype') as PypeHostElement;
        createdNew = true;
        container.appendChild(psPype);
      }

      if (config.useIframe) {
        psPype.setAttribute('use-iframe', 'true');
      }

      const onLauncherReady = () =>
        this._emitSafe('launcher:ready', undefined as never);
      const onMessengerReady = () =>
        this._emitSafe('messenger:ready', undefined as never);
      psPype.addEventListener('pype-launcher-ready', onLauncherReady);
      psPype.addEventListener('pype-messenger-ready', onMessengerReady);

      const onChatReady = () => {
        this._emitSafe('setup:complete', undefined as never);
        // Single flush point for buffered open() — boot tail is too early (runtime not ready yet).
        // Also re-fires after restart so open() during reconnect flushes correctly.
        if (this._pendingOpen) {
          this._pendingOpen = false;
          this._psPype?.openMessenger?.();
        }
      };
      const onFirstMessage = () =>
        this._emitSafe('message:first', undefined as never);
      psPype.addEventListener('pype-chat-ready', onChatReady);
      psPype.addEventListener('pype-first-message', onFirstMessage);

      const onSessionCreated = (e: Event) => {
        const { detail } = e as CustomEvent;
        // New chatId → reset per-conversation metadata cap.
        if (detail.chatId !== this._sessionData?.chatId) {
          this._metadataSessionCount = 0;
        }
        this._sessionData = {
          accessToken: detail.accessToken,
          chatId: detail.chatId,
          pypeId: detail.pypeId,
          streamId: detail.streamId,
          userId: detail.userId,
        };
        setStoredSession(config.appId!, {
          accessToken: detail.accessToken,
          chatId: detail.chatId,
          userId: detail.userId,
          pypeId: detail.pypeId,
          streamId: detail.streamId,
          maskingRules: detail.maskingRules,
        });
        this._emitSafe('chat:start', { conversationId: detail.chatId });
        // restart() from chat_ended re-activates the Loader; otherwise emit
        // state:change directly for a normal in-session new chatId.
        if (this._status === LoaderStatus.ChatEnded) {
          this._transition(LoaderStatus.ChatActive);
        } else {
          this._emitStateChange();
        }
      };

      const onSessionCleared = () => {
        this._sessionData = null;
        clearStoredSession(config.appId!);
      };

      psPype.addEventListener('pype-session-created', onSessionCreated);
      psPype.addEventListener('pype-session-cleared', onSessionCleared);

      // Widget-runtime errors bubble up as `pype-error` CustomEvents; forward
      // as zone:'widget' so onError + 'error' bus see them alongside Loader errors.
      const onWidgetError = (e: Event) => {
        const detail = (e as CustomEvent).detail as
          | {
              code?: string;
              message?: string;
              originalError?: unknown;
            }
          | undefined;
        const code = (detail?.code ??
          'UNKNOWN_WIDGET_ERROR') as WidgetErrorCode;
        const message = detail?.message ?? 'Widget runtime error';
        this._reportError({
          zone: 'widget',
          code,
          message,
          originalError: detail?.originalError,
          timestamp: Date.now(),
        });
      };
      psPype.addEventListener('pype-error', onWidgetError);

      // Component failures bubble as `pype-component-failed`; forward as zone:'embed'.
      // These never transition Loader state — one bad component never kills the chat.
      const onEmbedError = (e: Event) => {
        const detail = (e as CustomEvent).detail as
          | {
              code?: string;
              componentType?: string;
              messageId?: string;
              instanceId?: string;
              message?: string;
              cause?: { name: string; message: string };
            }
          | undefined;
        if (!detail) return;
        const code = (detail.code ??
          'COMPONENT_RENDER_FAILED') as EmbedErrorCode;
        this._reportError({
          zone: 'embed',
          code,
          componentType: detail.componentType ?? '',
          messageId: detail.messageId ?? '',
          instanceId: detail.instanceId ?? '',
          message: detail.message ?? 'Component failure',
          cause: detail.cause,
          timestamp: Date.now(),
        });
      };
      psPype.addEventListener('pype-component-failed', onEmbedError);

      const onViewState = (e: Event) => {
        const { detail } = e as CustomEvent<PypeViewState>;
        if (!detail) return;
        this._handleViewState(detail);
      };
      psPype.addEventListener('pype-view-state', onViewState);

      const elForCleanup = psPype;
      this._readyListenerCleanup = () => {
        elForCleanup.removeEventListener(
          'pype-launcher-ready',
          onLauncherReady
        );
        elForCleanup.removeEventListener(
          'pype-messenger-ready',
          onMessengerReady
        );
        elForCleanup.removeEventListener('pype-chat-ready', onChatReady);
        elForCleanup.removeEventListener('pype-first-message', onFirstMessage);
        elForCleanup.removeEventListener(
          'pype-session-created',
          onSessionCreated
        );
        elForCleanup.removeEventListener(
          'pype-session-cleared',
          onSessionCleared
        );
        elForCleanup.removeEventListener('pype-error', onWidgetError);
        elForCleanup.removeEventListener('pype-component-failed', onEmbedError);
        elForCleanup.removeEventListener('pype-view-state', onViewState);
      };

      // Adopting an already-running popup: the widget is live with its own
      // session — re-wire the loader→element event bridge above, but do NOT
      // set config/session or call initialize() (that would restart the chat).
      if (!opts?.adopt) {
        psPype.config = this._widgetConfig(config);
        psPype.session = storedSession
          ? {
              accessToken: storedSession.accessToken,
              chatId: storedSession.chatId,
              pypeId: storedSession.pypeId,
              streamId: storedSession.streamId,
              userId: storedSession.userId,
              maskingRules: storedSession.maskingRules,
            }
          : null;
        psPype.initialize?.();
      }

      this._psPype = psPype;
    } catch (cause) {
      // Partial-DOM cleanup: undo anything we did so the host DOM isn't left
      // with an orphan <ps-pype>.
      try {
        this._readyListenerCleanup?.();
      } catch {
        /* never let cleanup itself throw further */
      }
      this._readyListenerCleanup = null;

      // Only remove the element if WE created it — a pre-existing <ps-pype>
      // belongs to the host or a previous mount.
      if (createdNew && psPype && psPype.parentNode === container) {
        try {
          container.removeChild(psPype);
        } catch {
          /* ignore — best-effort cleanup */
        }
      }

      throw cause;
    }
  }

  private _unmountElement(): void {
    // Window target: close the popup we opened (intentional teardown from
    // shutdown/reset/unmount). Null it FIRST so the close-watcher's `onClose`
    // (which bails when `_windowTarget` is null) can't double-fire. Emit
    // `window:close` so hosts see one close signal for every teardown path.
    if (this._windowTarget) {
      const wt = this._windowTarget;
      this._windowTarget = null;
      this._emitSafe('window:close', undefined as never);
      try {
        wt.close();
      } catch {
        /* best-effort */
      }
    }

    // Each step is wrapped independently so a throw in one doesn't block the
    // others. unmount() / shutdown() / reset() rely on this never throwing —
    // a throw here would escape into the host's stack and violate the
    // host-safety contract.
    try {
      this._readyListenerCleanup?.();
    } catch (cause) {
      this._reportError(
        new PypeLoaderError(
          LoaderErrorCode.UNKNOWN,
          'Error while removing <ps-pype> event listeners.',
          { cause: cause instanceof Error ? cause : new Error(String(cause)) }
        )
      );
    }
    this._readyListenerCleanup = null;

    try {
      this._psPype?.remove();
    } catch (cause) {
      this._reportError(
        new PypeLoaderError(
          LoaderErrorCode.UNKNOWN,
          'Error while removing <ps-pype> from DOM.',
          { cause: cause instanceof Error ? cause : new Error(String(cause)) }
        )
      );
    }
    this._psPype = null;

    // Tear down the deprecated event bridge. Done here (not in _clearPendingIntents,
    // which fires on Idle→Initializing) so pre-boot registerForEvents registrations
    // survive boot. teardown() empties the tracking array so reset→reboot→re-register
    // can't double-attach on window.
    try {
      this._eventBridge?.teardown();
    } catch {
      /* never let bridge cleanup throw into the host stack */
    }
    this._eventBridge = null;
    this._pendingMetadata = [];
    this._metadataSessionCount = 0;
    this._metadataWindowStart = 0;
    this._metadataWindowCount = 0;
    this._metadataRateWarned = false;
  }

  private _transition(to: LoaderStatus): void {
    const allowed = ALLOWED_TRANSITIONS[this._status];
    if (!allowed.includes(to)) {
      throw new PypeLoaderError(
        LoaderErrorCode.INVALID_TRANSITION,
        `Invalid transition: ${this._status} → ${to}`,
        { loaderStatus: this._status }
      );
    }
    this._status = to;
    // Clear buffered open intent on any non-ChatActive transition; prevents a
    // pre-boot button click from silently opening the messenger on a future boot.
    if (to !== LoaderStatus.ChatActive) {
      this._clearPendingIntents();
    }
    this._emitStateChange();
  }

  /**
   * Throw if the command isn't allowed in the current lifecycle STATUS.
   * Status-only by design — modality and runtime-readiness are softer failures
   * handled by `_guardModality` / `_guardReady` (warn + no-op), so a drop-in
   * caller that calls `toggle()` in the wrong modality or before the runtime is
   * ready doesn't get an exception. `can()` layers all three for host gating.
   */
  private _assertCommand(command: LoaderCommand): void {
    if (!ALLOWED_COMMANDS[this._status].includes(command)) {
      throw new PypeLoaderError(
        LoaderErrorCode.INVALID_STATE,
        `${command}() called in invalid state: ${this._status}.`,
        { loaderStatus: this._status }
      );
    }
  }

  /**
   * `applyStyles` / `applyBranding` are designer-preview-only. Their JSDoc has
   * always said "always paired with `skipDesignFetch`"; this makes that
   * executable.
   *
   * Not cosmetic. `applyStyles` is a full SNAPSHOT, not a patch — a caller
   * passing `{ rawCss }` alone wipes theme and tokens and unstyles the whole
   * widget. On a live embed the call also races the saved design that boot is
   * applying, so the result depends on which lands last. Failing loudly is the
   * protection; a warning would be read by nobody and the widget would still
   * be unstyled.
   *
   * `INVALID_CONFIG`, not `INVALID_STATE`: the loader is in a perfectly valid
   * state, and a caller catching `INVALID_STATE` to mean "not booted yet"
   * must not be handed this.
   */
  private _assertDesignerPreview(command: LoaderCommand): void {
    if (this._config.skipDesignFetch === true) return;
    throw new PypeLoaderError(
      LoaderErrorCode.INVALID_CONFIG,
      `${command}() is designer-preview only and requires \`skipDesignFetch: true\`. ` +
        `A live embed's styling comes from the saved design fetched at boot; ` +
        `${command}() replaces it wholesale and would race that fetch.`,
      { loaderStatus: this._status }
    );
  }

  private _emitStateChange(): void {
    const state = this.getState();
    if (this._config.debug) {
      // eslint-disable-next-line no-console
      console.debug('[pype-loader] state →', state.status, state);
    }
    this._emitSafe('state:change', state);
  }

  private _emitSafe<E extends keyof LoaderEvents>(
    event: E,
    payload: LoaderEvents[E]
  ): void {
    try {
      this._emitter.emit(event, payload);
    } catch {
      // Consumer callback errors must not crash the Loader
    }
  }

  /**
   * Diff a coalesced `pype-view-state` payload against the last-seen state
   * and fan out to the granular `messenger:*` / `launcher-state:change` events.
   *
   * **Initial-emit policy:** the very first view-state after boot is treated
   * as an initial sync, not a transition. We do NOT fire `messenger:close`
   * just because the boot snapshot reports `isOpen: false` — the messenger
   * was never "open" to begin with, so a `messenger:close` would be a
   * phantom event that breaks analytics consumers. Symmetrically, we only
   * fire `messenger:open` on the initial sync if the messenger actually IS
   * open (which is legitimate — e.g. session restore re-opens the panel).
   * `launcher-state:change` only fires on the initial
   * sync if the values differ from defaults (false / false), so a
   * fresh boot doesn't spam consumers with no-op defaults.
   */
  private _handleViewState(next: PypeViewState): void {
    const prev = this._lastViewState;
    this._lastViewState = next;

    // Conversation-ended edge: the first time the widget reports `ended`
    // (agent/server end, an ended snapshot, or `endChat()`), surface a single
    // `chat:end` and move to `chat_ended`. The widget stays mounted on the
    // chat-ended screen — this is the soft end, distinct from `shutdown()`
    // (which also unmounts + clears the session). Guarded on `ChatActive` so
    // it fires exactly once per conversation.
    const becameEnded = next.ended && (!prev || !prev.ended);
    if (becameEnded && this._status === LoaderStatus.ChatActive) {
      this._transition(LoaderStatus.ChatEnded);
      this._emitSafe('chat:end', undefined as never);
    }

    // Pending-end edge. Placed above the `!prev` return, like `becameEnded`, so a
    // session restored mid-request still tells the host. The latch — not a
    // `prev`/`next` comparison — is what makes this once-per-ask: see
    // `_endRequestedEmitted`.
    if (next.endRequested && !this._endRequestedEmitted) {
      this._endRequestedEmitted = true;
      this._emitSafe('chat:end-requested', undefined as never);
    }
    // Clear on the resolution, not on `endRequested` going false: a reconnect
    // also makes it false, and re-arming there is exactly the double-fire.
    if (becameEnded) {
      this._endRequestedEmitted = false;
    }

    if (!prev) {
      // Initial sync — emit only meaningful non-default values.
      if (next.isOpen) {
        this._emitSafe('messenger:open', undefined as never);
      }
      if (next.busy || next.hasError) {
        this._emitSafe('launcher-state:change', {
          busy: next.busy,
          hasError: next.hasError,
        });
      }
      return;
    }

    if (prev.isOpen !== next.isOpen) {
      if (next.isOpen) {
        this._emitSafe('messenger:open', undefined as never);
      } else {
        this._emitSafe('messenger:close', undefined as never);
      }
    }
    if (prev.busy !== next.busy || prev.hasError !== next.hasError) {
      this._emitSafe('launcher-state:change', {
        busy: next.busy,
        hasError: next.hasError,
      });
    }
  }

  /**
   * Clear any buffered open intent. Called from any state transition that
   * isn't reaching `ChatActive`, plus explicit teardown paths (`shutdown`,
   * `reset`, `close`). Prevents a stale pre-boot intent from opening the
   * messenger in a fresh session.
   */
  private _clearPendingIntents(): void {
    this._pendingOpen = false;
  }

  /**
   * Single sink for Loader-zone and widget-zone errors.
   *
   * Mirrors {@link _emitSafe}: fires the consumer-supplied `onError` callback
   * (wrapped so consumer throws never break the Loader), emits the `'error'`
   * event on the bus, and logs to console. All three sinks fire on every
   * call — consumers pick whichever ergonomics suit their code.
   *
   * Accepts either:
   * - A {@link PypeLoaderError} or unknown thrown value — wrapped as `zone:'loader'`.
   * - A pre-built {@link PypeError} (e.g. forwarded from a `pype-error` event
   *   dispatched by `<ps-pype>`) — passed through with its existing `zone`.
   *
   * Console logging stays on in production: silent failure is anti-pattern;
   * the host-safety contract is "don't *throw* into host", not "don't *speak*".
   */
  private _reportError(err: PypeError | PypeLoaderError | unknown): void {
    let pypeError: PypeError;
    let bridgeForBus: PypeLoaderError;

    if (this._isPypeError(err)) {
      pypeError = err;
      // Re-package as PypeLoaderError for the (legacy) 'error' event bus.
      // Embed-zone errors have a sanitized `cause`; widget/loader have
      // `originalError`. Funnel both into a single `cause: Error | undefined`.
      let cause: Error | undefined;
      if (err.zone === 'embed') {
        cause = err.cause ? new Error(err.cause.message) : undefined;
      } else if (err.originalError instanceof Error) {
        cause = err.originalError;
      }
      bridgeForBus = new PypeLoaderError(
        err.zone === 'loader' ? err.code : LoaderErrorCode.UNKNOWN,
        err.message,
        { cause }
      );
    } else {
      const sdkError =
        err instanceof PypeLoaderError
          ? err
          : new PypeLoaderError(
              LoaderErrorCode.UNKNOWN,
              err instanceof Error ? err.message : String(err),
              { cause: err instanceof Error ? err : undefined }
            );
      pypeError = {
        zone: 'loader',
        code: sdkError.code,
        message: sdkError.message,
        originalError: sdkError.cause,
        timestamp: Date.now(),
      };
      bridgeForBus = sdkError;
    }

    if (this._onError) {
      try {
        this._onError(pypeError);
      } catch {
        // Consumer onError callback errors must not crash the Loader
      }
    }

    this._emitSafe('error', bridgeForBus);

    // eslint-disable-next-line no-console
    console.error('[pype-loader]', pypeError.zone, pypeError.code, pypeError);
  }

  private _isPypeError(value: unknown): value is PypeError {
    return (
      typeof value === 'object' &&
      value !== null &&
      'zone' in value &&
      'code' in value &&
      'message' in value &&
      'timestamp' in value
    );
  }

  /**
   * The config handed to `<ps-pype>`. The `window` popup options are Loader-only
   * mechanics (size/name/URL) so they're stripped. `target` IS forwarded — the
   * widget needs to know it lives in a popup so its shell can drop the
   * minimize/close chrome (the OS window handles those).
   *
   * For `target: 'window'` we also force `launcher: 'none'`: a popup has no
   * host page to anchor a floating launcher FAB to, so the messenger/shell
   * should fill the popup directly (the same "headless" layout the iframe path
   * uses). Fullpage already has no launcher, so this only affects floating.
   */
  private _widgetConfig(config: AppConfig): AppConfig {
    const { window: _window, ...rest } = config;
    if (rest.target === 'window') {
      return { ...rest, launcher: 'none' };
    }
    return rest;
  }

  /**
   * The `window` target's popup was closed by the USER (X button / OS close),
   * not via a Loader teardown. Emit `window:close`, detach the now-dead element
   * wiring, and move to `chat_ended` so `boot()` can restart. The session is
   * KEPT (not cleared) so a re-boot restores the conversation — the host
   * decides whether to reopen (e.g. re-`boot()` inline or in a new window).
   */
  private _handleWindowClosed(): void {
    // Intentional teardowns null `_windowTarget` before closing, so a null
    // here means this is the user-initiated close path.
    if (!this._windowTarget) return;
    this._windowTarget = null;

    this._emitSafe('window:close', undefined as never);

    try {
      this._readyListenerCleanup?.();
    } catch {
      /* never throw into the host stack */
    }
    this._readyListenerCleanup = null;
    this._psPype = null;
    this._lastViewState = null;
    this._endRequestedEmitted = false;
    this._clearPendingIntents();
    this._bootPromise = null;

    // Closing the popup IS the end-of-chat action in window mode (there is no
    // in-widget end affordance — the OS window owns close). The in-popup machine
    // is torn down with the realm, so it never flushes the session on end;
    // clear it here so a re-`boot()` starts a FRESH conversation rather than
    // resuming the one the user closed. A refresh (onReload) is a separate signal
    // and still restores — this only fires on a genuine close.
    clearStoredSession(this._config.appId!);
    this._sessionData = null;

    if (this._status === LoaderStatus.ChatActive) {
      this._transition(LoaderStatus.ChatEnded);
      this._emitSafe('chat:end', undefined as never);
    }
  }

  /**
   * The `window` popup was refreshed by the user. The `document.write`'d
   * `about:blank` reloads to a blank page (the bootstrap doesn't re-run), so the
   * old `<ps-pype>` is dead. Decide:
   * - **Active chat → restore:** re-inject the bootstrap (reuses the original
   *   nonce) and re-acquire the element once the popup re-imports the runtime;
   *   the session carries over via shared `localStorage`.
   * - **Not active (ended / errored) → tear down:** don't resurrect an ended
   *   conversation — mirror the user-close path (Option A).
   */
  private _handleWindowReload(): void {
    if (!this._windowTarget || this._windowTarget.closed) return;

    // Re-mount the widget so a refresh never leaves a blank popup. For an
    // ACTIVE chat the conversation resumes from the stored session. For an
    // ENDED chat the session was already flushed on end (by design — the
    // chat-ended menu is in-memory only), so the widget boots a FRESH
    // conversation — the same thing an inline host-page refresh does after an
    // ended chat; the fresh session re-activates the Loader via
    // `pype-session-created`. Abnormal states (idle/error with a live popup)
    // can't be re-mounted, so tear down instead.
    if (
      this._status === LoaderStatus.ChatActive ||
      this._status === LoaderStatus.ChatEnded
    ) {
      // `_remountWindow` owns the reinject → await-handshake → mount sequence.
      void this._remountWindow();
      return;
    }

    this._handleWindowClosed();
  }

  /**
   * Re-acquire `<ps-pype>` in the freshly-reloaded popup: re-inject the
   * bootstrap, AWAIT the re-injected runtime's handshake, then re-mount into the
   * popup body. An active chat is restored from the stored session; an ended
   * chat has no stored session (flushed on end) so the widget boots fresh.
   *
   * Awaiting `reinject()` is what makes a FAILED re-import recoverable: it
   * rejects on the `pype-window-error` handshake (or a stalled-import timeout /
   * popup close), so we fall through to the close path instead of hanging on
   * `whenDefined('ps-pype')` forever with a blank popup — the reload-path
   * analogue of how the initial boot gates on the `ready` handshake.
   */
  private async _remountWindow(): Promise<void> {
    if (!this._windowTarget || this._windowTarget.closed) return;

    try {
      this._readyListenerCleanup?.();
    } catch {
      /* never throw into the host stack */
    }
    this._readyListenerCleanup = null;
    this._psPype = null;
    this._lastViewState = null;
    this._endRequestedEmitted = false;

    const config = this._config;
    const storedSession = getStoredSession(config.appId!);
    const wasActive = this._status === LoaderStatus.ChatActive;
    try {
      // Re-inject the bootstrap and wait for the popup to re-import the runtime.
      // Rejects (→ catch below) if the re-import fails, stalls, or the popup is
      // closed mid-reload.
      await this._windowTarget.reinject();
      // The popup may have been closed while we awaited the re-import.
      if (!this._windowTarget || this._windowTarget.closed) return;
      await this._mountElement(
        this._windowTarget.window.document.body,
        storedSession,
        { allowEnded: true }
      );
      // Re-arm reload detection for the next refresh.
      this._windowTarget?.notifyMounted();
      // Only signal chat:start for an active restore — an ended-chat refresh
      // re-shows the ended screen, it does not (re)start a conversation.
      if (storedSession && wasActive) {
        this._emitSafe('chat:start', { conversationId: storedSession.chatId });
      }
    } catch (cause) {
      this._reportError(
        new PypeLoaderError(
          LoaderErrorCode.MOUNT_FAILED,
          'Failed to re-mount <ps-pype> after the popup reloaded.',
          { cause: cause instanceof Error ? cause : new Error(String(cause)) }
        )
      );
      // Re-mount failed and the popup now shows the (blank) re-injected doc —
      // close it so we don't leave a dead window on screen, then run the
      // user-close teardown (emits window:close + chat:end). Close BEFORE
      // `_handleWindowClosed`, which nulls `_windowTarget`.
      try {
        this._windowTarget?.close();
      } catch {
        /* best-effort */
      }
      this._handleWindowClosed();
    }
  }

  private _toSessionInfo(): SessionInfo {
    return {
      chatId: this._sessionData?.chatId ?? '',
      pypeId: this._sessionData?.pypeId ?? '',
      streamId: this._sessionData?.streamId ?? '',
      userId: this._sessionData?.userId ?? '',
    };
  }
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create a new Pype Loader instance.
 *
 * @example
 * ```ts
 * import { createPypeLoader } from '@pypestream/pype-loader';
 *
 * const loader = createPypeLoader({
 *   appId: 'app_123',
 *   language: 'en',
 * });
 *
 * loader.on('state:change', (state) => console.log(state.status));
 * await loader.boot();
 * ```
 */
export function createPypeLoader(options: CreatePypeLoaderOptions): PypeLoader {
  return new PypeLoader(options);
}

// ---------------------------------------------------------------------------
// Iframe utilities (for Pype App layer integration)
// ---------------------------------------------------------------------------
//
// These are low-level postMessage helpers for internal iframe-bridge wiring,
// NOT part of the public embed API. They are exported so the app layer can
// reuse the wire format, but marked @internal so they're excluded from the
// generated docs (typedoc `excludeInternal`). Host pages never need them.

/**
 * Prefix prepended to all Loader iframe messages to distinguish them from other postMessage traffic.
 * @internal
 */
export const PYPE_LOADER_EVENT_PREFIX = 'pype-loader:';

/**
 * Create a serialized shutdown message string for posting to the iframe.
 * @internal
 */
export function createShutdownEvent(): string {
  return PYPE_LOADER_EVENT_PREFIX + JSON.stringify({ type: 'shutdown' });
}

/**
 * Parse an incoming postMessage `data` value into a typed Loader payload.
 *
 * Returns `null` if the message is not a recognized Loader event
 * (wrong prefix, invalid JSON, or unknown type).
 * @internal
 */
export function parseIframeMessage(
  data: unknown
): { type: string; [key: string]: unknown } | null {
  if (typeof data !== 'string' || !data.startsWith(PYPE_LOADER_EVENT_PREFIX)) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(
      data.slice(PYPE_LOADER_EVENT_PREFIX.length)
    );
    if (
      parsed &&
      typeof parsed === 'object' &&
      'type' in parsed &&
      typeof (parsed as { type: unknown }).type === 'string'
    ) {
      return parsed as { type: string; [key: string]: unknown };
    }
    return null;
  } catch {
    return null;
  }
}
